import { computed, inject, Injectable } from '@angular/core';
import { IrisDmsSidebarTabType } from '../models/IrisDmsSidebarTab';
import { IrisDmsSidebarStore } from '../store/dms-sidebar.signal-store';
import { Subject } from 'rxjs';
import { IrisDmsFolderInfoStore } from '../store/folder-info.signal-store';
import { IrisDmsFoldersSandbox } from './dms-folders.sandbox';

@Injectable()
export class IrisDmsSidebarSandbox {
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);

  private readonly dmsSidebarStore = inject(IrisDmsSidebarStore);
  private readonly folderInfoStore = inject(IrisDmsFolderInfoStore);

  public readonly isFolderSidebarShown = this.dmsSidebarStore.folderSidebarShown;
  public readonly isFileSidebarShown = this.dmsSidebarStore.fileSidebarShown;
  public readonly isSidebarShown = this.dmsSidebarStore.sidebarShown;
  public readonly isFileEdit = this.dmsSidebarStore.isFileEdit;
  public readonly selectedSidebarTabIndex = this.dmsSidebarStore.selectedTabIndex;
  public readonly selectedSidebarTabName = computed(() => this.getFolderTabNameByIndex(this.selectedSidebarTabIndex()));

  public readonly selectedFolderId = this.dmsSidebarStore.selectedFolderId;
  public readonly selectedFileId = this.dmsSidebarStore.selectedFileId;

  private readonly isCurrentFolderTrashBin = computed(() => this.dmsFoldersSandbox.selectedFolder()?.isTrashBin);

  public readonly isTrashBinSelected = computed(() => {
    const isFolderSidebarShown = this.isFolderSidebarShown();
    if (!isFolderSidebarShown) {
      return this.isCurrentFolderTrashBin();
    }
    return this.folderInfoStore.isTrashBin();
  });

  private readonly fileEditCanceledSubject = new Subject<void>();
  readonly fileEditCanceled$ = this.fileEditCanceledSubject.asObservable();
  private readonly fileEditSaveSubject = new Subject<void>();
  readonly fileEditSave$ = this.fileEditSaveSubject.asObservable();

  showFolderSidebar(folderId: string): void {
    this.dmsSidebarStore.showFolderSidebar(folderId);
  }

  showFileSidebar(fileId: string): void {
    this.dmsSidebarStore.showFileSidebar(fileId);
  }

  showFileEditSidebar(fileId: string): void {
    this.dmsSidebarStore.showFileEditSidebar(fileId);
  }

  hideSidebar(): void {
    this.dmsSidebarStore.hideSidebar();
  }

  closeFileEditSidebar(hideSidebar = false): void {
    this.dmsSidebarStore.cancelFileEdit(hideSidebar);
  }

  cancelFileEdit(): void {
    this.dmsSidebarStore.cancelFileEdit();
    this.fileEditCanceledSubject.next();
  }

  saveFileEdit(): void {
    this.fileEditSaveSubject.next();
  }

  setSelectedTabIndex(tabIndex: number): void {
    this.dmsSidebarStore.selectTab(tabIndex);
  }

  getFolderTabNameByIndex(index: number): IrisDmsSidebarTabType {
    switch (index) {
      case 1:
        return IrisDmsSidebarTabType.Permissions;
      case 0:
      default:
        return IrisDmsSidebarTabType.Details;
    }
  }

  getFolderTabIndexByName(tabName: IrisDmsSidebarTabType): number {
    switch (tabName) {
      case IrisDmsSidebarTabType.Permissions:
        return 1;
      case IrisDmsSidebarTabType.Details:
      default:
        return 0;
    }
  }

  getFileTabNameByIndex(index: number, hasAttachments = false): IrisDmsSidebarTabType {
    const indexToCheck = hasAttachments ? index : index + 1;
    switch (true) {
      case indexToCheck === 1 && hasAttachments:
        return IrisDmsSidebarTabType.Attachments;
      case indexToCheck === 2:
        return IrisDmsSidebarTabType.Links;
      case indexToCheck === 3:
        return IrisDmsSidebarTabType.History;
      case index === 0:
      default:
        return IrisDmsSidebarTabType.Details;
    }
  }

  getFileTabIndexByName(tabName: IrisDmsSidebarTabType, isTrashBin = false): number {
    switch (true) {
      case tabName === IrisDmsSidebarTabType.Links && isTrashBin:
        return -1;
      case tabName === IrisDmsSidebarTabType.Links && !isTrashBin:
      case tabName === IrisDmsSidebarTabType.History && isTrashBin:
        return 1;
      case tabName === IrisDmsSidebarTabType.History && !isTrashBin:
        return 2;
      case tabName === IrisDmsSidebarTabType.Details:
      default:
        return 0;
    }
  }

  checkIsFileEditAndCancel(): void {
    if (this.isFileEdit()) {
      this.cancelFileEdit();
    }
  }
}
